import {type AxiosInstance} from "axios";
import {createApi} from "../api";
import {type Customer} from "../model/crm/Customer.ts";
import {type CustomerView} from "../model/crm/CustomerView.ts";
import {type CustomerCommunicationsResponse} from "../model/crm/CustomerCommunication.ts";
import {type CustomerCampaignsResponse} from "../model/crm/Campaign.ts";
import {type UniqueNo} from "../model/core/UniqueNo.ts";
import {type Salutation} from "../model/user/Salutation.ts";
import type {PurchasableVehicles, VehiclePurchase} from "../model/crm/Vehicle.ts";
import {type PagingParameters} from "../model/core/PagingParameters.ts";
import {type OrderType} from "../model/core/SortingParameter.ts";
import {type OpportunitiesResponse, type OpportunityResponse} from "../model/crm/Opportunity.ts";
import {type Salesperson} from "../model/crm/Salesperson.ts";
import {type FilterBarDefinition, type FilterBarSelection} from "../model/filterBar/FilterBarDefinition.ts";

export interface CustomerDetailsRequest extends Customer {
    garageNo: UniqueNo | undefined;
    garageGroupNo: UniqueNo | undefined;
}

export interface AssembleCustomerResponse {
    customer: Customer;
    salutations: Salutation[];
    consentSources: string[];
    interests: string[];
    // Salesperson assignment (#56642): only users with the right to list users may choose.
    salesperson?: Salesperson | null;
    assignableSalespersons: Salesperson[];
    salespersonEditable: boolean;
}

export type CustomerOverviewEmptyStateHint = "ONLY_MINE_EMPTY";

export interface CustomerOverviewResponse {
    customers: Customer[];
    total: number;
    // Garage-level flag: no sales opportunity activated yet (#55243, warning banner).
    noActiveOpportunities?: boolean;
    // Filter bar and empty-list hint (#56642).
    filters?: FilterBarDefinition[];
    emptyStateHint?: CustomerOverviewEmptyStateHint | null;
}

export interface CustomerViewResponse {
    customer: CustomerView;
}

// The garage or garage group is not part of the query: the BFF takes it from the token (#56642).
export interface CustomerOverviewQuery {
    fuzzy: string;
    // left out = the BFF applies its default filters (#56642)
    filters?: FilterBarSelection;
    paging: PagingParameters;
    sorting?: {
        orders: Record<string, OrderType>;
    };
}

export interface AssembleCustomerRequest {
    customerNo: string | undefined;
    garageNo: string | undefined;
    garageGroupNo: string | undefined;
}

export interface AssembleVehiclePurchaseRequest {
    customerNo: UniqueNo;
    vehiclePurchaseNo: UniqueNo | undefined;
}

export interface AssembleVehiclePurchaseResponse {
    customerNo: string;
    vehicles: PurchasableVehicles[];
    purchase: VehiclePurchase | undefined;
}

export interface AssembleOpportunitiesRequest {
    garageNo: UniqueNo | undefined;
    garageGroupNo: UniqueNo | undefined;
}

export interface AssembleOpportunityRequest {
    garageNo: UniqueNo | undefined;
    garageGroupNo: UniqueNo | undefined;
    opportunityNo?: UniqueNo;
    template: boolean;
    type?: string;
}

export interface AssembleTrialMailRequest {
    garageNo: UniqueNo | null;
    garageGroupNo: UniqueNo | null;
}

export interface AssembleTrialMailResponse {
    vehicles: PurchasableVehicles[];
    interests: string[];
}

export interface SendTrialMailRequest {
    dealershipNo: UniqueNo;
    opportunityNo: UniqueNo;
    mailTo: string;
    stepType: string;
    carlineNo: string;
    vehicleName: string;
    brandName: string;
    interests: string[];
}

export type ActivateOpportunityRequest = AssembleOpportunityRequest;

class CrmService {
    api: AxiosInstance;

    constructor () {
        this.api = createApi("/b4cockpit/");
    }

    async getCustomers (query: CustomerOverviewQuery): Promise<CustomerOverviewResponse> {
        return await this.api.post("customers/overview", query).then(response => response.data);
    }

    async assembleCustomer (request: AssembleCustomerRequest): Promise<AssembleCustomerResponse> {
        const customerNo = request.customerNo;
        if (!customerNo) {
            return await this.api.get("/customer/assemble-new").then(response => response.data);
        }
        return await this.api.get(`/customer/${customerNo}/assemble`).then(response => response.data);
    }

    async establishCustomer (request: CustomerDetailsRequest): Promise<AssembleCustomerResponse> {
        return await this.api.post("/customer/establish", request).then(response => response.data);
    }

    async modifyCustomer (request: CustomerDetailsRequest): Promise<AssembleCustomerResponse> {
        return await this.api.post("/customer/modify", request).then(response => response.data);
    }

    async deleteCustomer (customerNo: UniqueNo): Promise<void> {
        await this.api.delete(`/customer/${customerNo.index}`);
    }

    async viewCustomer (customerNo: string): Promise<CustomerViewResponse> {
        return await this.api.get(`/customer/${customerNo}/view`).then(response => response.data);
    }

    async getCustomerCommunications (customerNo: string): Promise<CustomerCommunicationsResponse> {
        return await this.api.get(`/customer/${customerNo}/communications`).then(response => response.data);
    }

    async getCustomerCampaigns (customerNo: string): Promise<CustomerCampaignsResponse> {
        return await this.api.get(`/customer/${customerNo}/campaigns`).then(response => response.data);
    }

    async getVehicleOptions (customerNo: string): Promise<AssembleVehiclePurchaseResponse> {
        return await this.api.get("/customer/" + customerNo + "/vehicle/assemble")
            .then(response => response.data);
    }

    async assembleVehiclePurchase (vehiclePurchaseNo: string): Promise<AssembleVehiclePurchaseResponse> {
        return await this.api.get(`/customer/vehicle/${vehiclePurchaseNo}/assemble`).then(response => response.data);
    }

    async establishVehiclePurchase (customerNo: string, request: VehiclePurchase): Promise<VehiclePurchase> {
        const {
            brandName,
            vehicleName,
            carlineNo,
            dealershipNo,
            contractSignedAt,
            deliveredAt,
            deliveryEstimated
        } = request;
        return await this.api.post(`/customer/${customerNo}/vehicle/establish`, {
            brandName,
            vehicleName,
            carlineNo,
            dealershipNo,
            contractSignedAt,
            deliveredAt,
            deliveryEstimated
        }).then(response => {
            return {
                ...response.data,
                customerNo: {index: customerNo}
            };
        });
    }

    async modifyVehiclePurchase (request: VehiclePurchase): Promise<VehiclePurchase> {
        const {
            brandName,
            vehiclePurchaseNo,
            vehicleName,
            carlineNo,
            dealershipNo,
            contractSignedAt,
            deliveredAt,
            deliveryEstimated
        } = request;
        return await this.api.post(`/customer/vehicle/${vehiclePurchaseNo.index}`, {
            brandName,
            vehicleName,
            carlineNo,
            dealershipNo,
            contractSignedAt,
            deliveredAt,
            deliveryEstimated
        }).then(response => response.data);
    }

    async deleteVehiclePurchase (vehiclePurchaseNo: UniqueNo): Promise<void> {
        await this.api.delete(`/customer/vehicle/${vehiclePurchaseNo.index}`);
    }

    async assembleOpportunities (request: AssembleOpportunitiesRequest): Promise<OpportunitiesResponse> {
        return await this.api.post("/promotion/opportunities/assemble", request).then(response => response.data);
    }

    async assembleOpportunity (request: AssembleOpportunityRequest): Promise<OpportunityResponse> {
        return await this.api.post("/promotion/opportunity/assemble", request).then(response => response.data);
    }

    async activateOpportunity (request: ActivateOpportunityRequest): Promise<OpportunityResponse> {
        return await this.api.post("/promotion/opportunity/activate", request)
            .then(response => response.data);
    }

    async deactivateOpportunity (opportunityNo: UniqueNo | string): Promise<OpportunityResponse> {
        const opportunityId = typeof opportunityNo === "string" ? opportunityNo : opportunityNo.index;
        return await this.api.post(`/promotion/opportunity/${opportunityId}/deactivate`)
            .then(response => response.data);
    }

    async assembleTrialMail (request: AssembleTrialMailRequest): Promise<AssembleTrialMailResponse> {
        return await this.api.post("/promotion/trialmail/assemble", request)
            .then(response => response.data);
    }

    async sendTrialMail (request: SendTrialMailRequest): Promise<void> {
        return await this.api.post("/promotion/trialmail", request)
            .then(response => response.data);
    }
}

const crmServiceInstance = new CrmService();

export default crmServiceInstance;
